import { Stack } from '@mantine/core';
import { Banner, Button, Title } from '@repo/foundations';

import { useCheckSlackOauthScopes } from '@repo/api-codegen';
import type { ButtonDetails } from '@repo/common/components';
import { EmptyState } from '@repo/common/components';
import { integrationList } from '@repo/common/constants/integration/integrations';
import { width } from '@repo/theme/primitives';
import { isEmpty } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router';
import type { IIntegration } from '../../../api';
import { useAuthUser, useIntegrationList } from '../../../api';
import { useFeatureFlags } from '../../../utils/featureFlags';
import AddIntegrationModal from '../../Integration/AddIntegrationModal';
import { useStyles as useAddIntegrationModalStyles } from '../../Integration/AddIntegrationModal/AddIntegrationModal.styles';
import { NEW_INTEGRATION_MODAL_ID } from '../../Integration/AddIntegrationModal/constants';
import FullWidthLoadingSpinner from '../../LoadingSpinner';
import { openModal } from '../../ModalManager';
import { TableV2 } from '../../TableV2';
import type { OnCellClickHandlerParams } from '../../TableV2/types';
import { useActions, useColumns } from './ExtensionSettings.hooks';

const QUICK_ACTIONS = ['actions::delete'] as const;

const PAGINATION_LIST_OPTIONS = {
	suspense: true,
};

export function ExtensionSettings() {
	const { isAdminUser } = useAuthUser();
	const { servicenowIntegration, extensionsInIntegrationsPage } =
		useFeatureFlags();
	const { data: slackOAuthScopes } = useCheckSlackOauthScopes({});

	const SETTINGS_PAGE_ONLY_TYPES = useMemo(
		() =>
			integrationList
				.filter((i) => i.isExtension)
				.filter((i) => i.type !== 'servicenow' || servicenowIntegration)
				.map((i) => i.type),
		[servicenowIntegration]
	);

	const { data: integrations, isLoading } = useIntegrationList({
		page: 1,
		filters: {
			type__in: SETTINGS_PAGE_ONLY_TYPES.join(','),
		},
	});

	const { classes: addIntegrationModalClasses } =
		useAddIntegrationModalStyles();

	const columns = useColumns({
		readOnly: !isAdminUser,
	});
	const actions = useActions();

	const navigate = useNavigate();

	const handleClick = useCallback(
		(id: string) => {
			navigate(`/integrations/${id}`);
		},
		[navigate]
	);

	const handleConnectExtension = useCallback(() => {
		openModal({
			modalId: NEW_INTEGRATION_MODAL_ID,
			title: <Title size="md">Connect Extension</Title>,
			children: (
				<AddIntegrationModal
					integrationTypes={SETTINGS_PAGE_ONLY_TYPES}
					modalType="extension"
				/>
			),
			size: width.xxl,
			radius: 'lg',
			classNames: {
				header: addIntegrationModalClasses.header,
				body: addIntegrationModalClasses.body,
			},
		});
	}, [
		SETTINGS_PAGE_ONLY_TYPES,
		addIntegrationModalClasses.body,
		addIntegrationModalClasses.header,
	]);

	const onCellClick = useCallback(
		(params: OnCellClickHandlerParams<IIntegration>) => {
			if (params.column.navigate !== false) {
				handleClick(params.record.id);
			}
		},
		[handleClick]
	);

	if (isLoading) {
		return <FullWidthLoadingSpinner />;
	}

	if (!isLoading && isEmpty(integrations)) {
		const buttons: ButtonDetails[] = [
			{
				name: 'Learn more',
				action: () => {
					window.open('https://docs.secoda.com/integrations', '_blank');
				},
				isPrimary: false,
				size: 'md',
				leftIconName: 'externalLink',
			},
			{
				name: 'Connect extension',
				action: handleConnectExtension,
				isPrimary: true,
				size: 'md',
			},
		];

		return (
			<EmptyState
				title="Connect your first extension"
				description="Set up two or more extensions to seamlessly interact, govern and view lineage with your data."
				illustrationName="integrations"
				includeGoBack={false}
				size="lg"
				buttons={buttons}
				stateHeight="80vh"
			/>
		);
	}

	return (
		<Stack>
			{slackOAuthScopes?.requires_reinstallation && (
				<Banner
					message={`Your Slack extension is missing the following scopes: ${slackOAuthScopes?.missing_scopes.join(', ')}. Re-authorize the extension to continue using it`}
					tone="warning"
					actions={[
						{
							children: 'Re-authorize',
							onClick: () => {
								navigate(`/integrations/${slackOAuthScopes?.id}`);
							},
						},
					]}
				/>
			)}
			<TableV2<IIntegration>
				pluralTypeString="extensions"
				columns={columns}
				usePaginationList={useIntegrationList}
				defaultRequiredSearchParams={{
					type__in: SETTINGS_PAGE_ONLY_TYPES.join(','),
				}}
				usePaginationListOptions={PAGINATION_LIST_OPTIONS}
				withActions={actions}
				withCheckbox={true}
				withQuickActions={QUICK_ACTIONS}
				withInteractiveHeader={extensionsInIntegrationsPage}
				withSearch={extensionsInIntegrationsPage}
				withAdditionalButtons={
					!extensionsInIntegrationsPage &&
					isAdminUser && (
						<Button onClick={handleConnectExtension} variant="primary">
							Connect extension
						</Button>
					)
				}
				onCellClick={onCellClick}
			/>
		</Stack>
	);
}

export default ExtensionSettings;
